import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Calendar, User, DollarSign, 
  FileText, Plus, CheckCircle2, History,
  ChevronDown, Search
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { HouseholdContact } from '@/types';

interface HistoricalTaskCreatorProps {
  onAdd: (data: {
    title: string;
    description: string;
    date: string;
    cost: string;
    assignedContactId: string;
    providerName: string;
    category: string;
    isHistorical: boolean;
  }) => void;
  onClose: () => void;
  contacts?: HouseholdContact[]; // 🛰️ Optional to prevent crash on boot
}

/**
 * 🛠️ HistoricalTaskCreator
 * Specialized slide-over for logging past maintenance work or planning future rhythms.
 */
export const HistoricalTaskCreator: React.FC<HistoricalTaskCreatorProps> = ({ 
  onAdd, 
  onClose,
  contacts = [] // 🛰️ Default to empty array to satisfy .filter
}) => {
  const { t } = useTranslation();
  
  const [isHistorical, setIsHistorical] = useState(true);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    cost: '',
    assignedContactId: '', // 🛰️ Refers to Contact Registry ID
    providerName: '',      // Fallback for manual text entry
    category: 'Maintenance'
  });

  // 🛰️ Explicitly typed filter parameter (Fixes TS7006)
  const filteredContacts = (contacts || []).filter((c: HouseholdContact) => 
    c.name.toLowerCase().includes(contactSearch.toLowerCase()) ||
    c.companyName?.toLowerCase().includes(contactSearch.toLowerCase())
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd({
      ...formData,
      isHistorical,
    });
    onClose();
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-slate-950 p-8 w-full max-w-xl shadow-2xl animate-in slide-in-from-right duration-300">
      <header className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic leading-none">
              {isHistorical ? t('maint.log_work', 'Log Past Work') : t('maint.plan_work', 'Plan Future Work')}
            </h2>
            <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.3em] mt-2">
              {t('maint.ledger_entry', 'Maintenance Ledger Entry')}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
            <Plus className="w-6 h-6 rotate-45 text-slate-400" />
          </button>
        </div>

        <nav className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl mt-8 border border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={() => setIsHistorical(false)}
            className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${!isHistorical ? 'bg-white dark:bg-slate-700 shadow-md text-indigo-600' : 'text-slate-400'}`}
          >
            <Calendar className="w-4 h-4" /> {t('maint.planned', 'Planned')}
          </button>
          <button
            type="button"
            onClick={() => setIsHistorical(true)}
            className={`flex-1 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${isHistorical ? 'bg-white dark:bg-slate-700 shadow-md text-indigo-600' : 'text-slate-400'}`}
          >
            <History className="w-4 h-4" /> {t('maint.logged', 'Historical')}
          </button>
        </nav>
      </header>

      <form onSubmit={handleSubmit} className="flex-1 space-y-6 overflow-y-auto no-scrollbar pb-12">
        <div className="space-y-4">
          <input
            required
            type="text"
            placeholder={t('maint.task_title', 'Task Title')}
            value={formData.title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, title: e.target.value })}
            className="w-full text-2xl font-black bg-transparent border-b-2 border-slate-100 dark:border-slate-800 focus:border-indigo-500 outline-none pb-2 placeholder:text-slate-200 uppercase italic tracking-tighter text-slate-900 dark:text-white"
          />
          <textarea
            placeholder={t('maint.description', 'Describe the work performed...')}
            value={formData.description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, description: e.target.value })}
            className="w-full bg-slate-50 dark:bg-slate-900 p-4 rounded-2xl text-sm font-bold min-h-[100px] outline-none border border-transparent focus:border-slate-200 dark:text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase text-slate-400 ml-2">{t('maint.date', 'Date')}</label>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="date"
                value={formData.date}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, date: e.target.value })}
                className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 font-black text-xs uppercase dark:text-white"
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase text-slate-400 ml-2">{t('maint.cost', 'Total Cost')}</label>
            <div className="relative">
              <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="number"
                placeholder="0.00"
                value={formData.cost}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, cost: e.target.value })}
                className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 font-black text-xs uppercase dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* --- 👷 DYNAMIC CONTACT PICKER --- */}
        <div className="space-y-2 relative">
          <label className="text-[10px] font-black uppercase text-slate-400 ml-2">{t('maint.provider', 'Service Provider')}</label>
          <button 
            type="button"
            onClick={() => setShowContactPicker(!showContactPicker)}
            className="w-full pl-12 pr-4 py-4 bg-slate-50 dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 font-black text-xs text-left flex justify-between items-center uppercase dark:text-white"
          >
            <div className="flex items-center gap-2">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              {formData.providerName || t('maint.select_contact', 'Select Contact...')}
            </div>
            <ChevronDown className="w-4 h-4 text-slate-400" />
          </button>

          {showContactPicker && (
            <div className="absolute z-50 bottom-full left-0 w-full mb-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl p-4 animate-in fade-in zoom-in-95 duration-200">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input 
                  autoFocus
                  className="w-full pl-9 pr-4 py-3 bg-slate-50 dark:bg-slate-800 rounded-xl text-xs font-bold outline-none focus:ring-1 ring-indigo-500"
                  placeholder="Search contacts..."
                  value={contactSearch}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContactSearch(e.target.value)}
                />
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1 no-scrollbar">
                {filteredContacts.map((contact: HouseholdContact) => (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, assignedContactId: contact.id, providerName: contact.name });
                      setShowContactPicker(false);
                    }}
                    className="w-full text-left p-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl flex justify-between items-center group transition-colors"
                  >
                    <div>
                      <p className="font-black text-[10px] text-slate-800 dark:text-slate-200 uppercase">{contact.name}</p>
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">{contact.companyName}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-8 border-2 border-dashed border-slate-100 dark:border-slate-800 rounded-[2.5rem] flex flex-col items-center justify-center text-center group hover:border-indigo-300 transition-all cursor-pointer bg-slate-50/50 dark:bg-slate-900/50">
          <div className="p-4 bg-white dark:bg-slate-800 rounded-full mb-3 group-hover:bg-indigo-50 transition-colors shadow-sm">
            <FileText className="w-6 h-6 text-slate-300 group-hover:text-indigo-500" />
          </div>
          <p className="text-[10px] font-black uppercase text-slate-400 group-hover:text-indigo-600 tracking-widest transition-colors">
            {t('maint.attach_invoice', 'Attach Registry Asset')}
          </p>
        </div>
      </form>

      <footer className="pt-6 border-t border-slate-100 dark:border-slate-800">
        <Button 
          onClick={handleSubmit}
          className="w-full h-16 rounded-[2rem] bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-widest gap-3 shadow-xl shadow-indigo-500/30"
        >
          {isHistorical ? <CheckCircle2 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          {isHistorical ? t('maint.commit_ledger', 'Commit to Ledger') : t('maint.schedule_task', 'Schedule Rhythm')}
        </Button>
      </footer>
    </div>
  );
};